import { useMutation } from '@tanstack/react-query'
import { consoleApi, getApiErrorMessage } from '@milliswift/api-client'
import {
  Badge,
  Button,
  Callout,
  CopyButton,
  EmptyState,
  Kbd,
  PreviewDataBadge,
  SectionHeader,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@milliswift/ui'
import Editor from '@monaco-editor/react'
import { Play } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { QueryPlan, QueryResult } from '@milliswift/types'
import { useDb } from '../lib/db'

/**
 * The SQL editor.
 *
 * ⚠️  POST /api/v1/query is not implemented on the platform yet
 * (docs/PLATFORM_API_GAPS.md); results come from the preview fixtures and the
 * page says so. The editor, results grid, and plan view are complete, so
 * connecting the real endpoint is a base-URL change.
 */

interface HistoryEntry {
  id: string
  sql: string
  duration_ms: number
  row_count: number
  at: string
}

const SAMPLE_SQL = 'SELECT status, COUNT(*) AS total\nFROM orders\nWHERE created_at > now() - interval \'30 days\'\nGROUP BY status\nORDER BY total DESC;'

export default function QueryPage() {
  const { databaseId } = useDb()
  const toast = useToast()

  const [sql, setSql] = useState(SAMPLE_SQL)
  const [result, setResult] = useState<QueryResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [page, setPage] = useState(0)

  const ROWS_PER_PAGE = 100

  const run = useMutation({
    mutationFn: (statement: string) =>
      consoleApi.runQuery({
        database_id: databaseId ?? '',
        sql: statement,
        max_rows: 1000,
      }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
      setPage(0)
      setHistory((current) => [
        {
          id: crypto.randomUUID(),
          sql: statementForHistory.current,
          duration_ms: data.duration_ms,
          row_count: data.row_count,
          at: new Date().toLocaleTimeString(),
        },
        ...current,
      ].slice(0, 50))
    },
    onError: (caught) => {
      setResult(null)
      setError(getApiErrorMessage(caught))
    },
  })

  // The mutation closes over the SQL at call time; track it for history.
  const statementForHistory = useRef(sql)
  statementForHistory.current = sql

  const runStatement = useCallback(
    (statement?: string) => {
      const toRun = statement ?? sql
      if (!toRun.trim()) {
        toast.info('Nothing to run', 'Type a query first.')
        return
      }
      statementForHistory.current = toRun
      run.mutate(toRun)
    },
    [run, sql, toast]
  )

  // ⌘/Ctrl+Enter runs.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        runStatement()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [runStatement])

  const pagedRows = result
    ? result.rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    : []
  const pageCount = result ? Math.ceil(result.rows.length / ROWS_PER_PAGE) : 0

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Query"
        description="Standard SQL against the active database."
        action={<PreviewDataBadge />}
      />

      {/* Editor */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => runStatement()}
              loading={run.isPending}
              leftIcon={<Play className="h-3.5 w-3.5" />}
            >
              Run
            </Button>
            <span className="hidden text-caption text-subtle sm:inline-flex items-center gap-1">
              <Kbd>⌘</Kbd> <Kbd>↵</Kbd>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSql('')
                setResult(null)
                setError(null)
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        <div className="h-[38vh] min-h-64">
          <Editor
            height="100%"
            defaultLanguage="sql"
            theme="vs-dark"
            value={sql}
            onChange={(value) => setSql(value ?? '')}
            options={{
              fontSize: 13,
              fontFamily: "'Geist Mono', ui-monospace, Menlo, monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
              padding: { top: 12, bottom: 12 },
            }}
          />
        </div>
      </div>

      {/* Results */}
      {run.isPending ? (
        <div className="flex items-center justify-center rounded-card border border-line bg-surface py-12 shadow-card">
          <Spinner className="h-5 w-5 text-subtle" />
        </div>
      ) : error ? (
        <Callout tone="danger" title="Query error">
          <p className="font-mono">{error}</p>
        </Callout>
      ) : result ? (
        <Tabs defaultValue="results">
          <TabsList>
            <TabsTrigger value="results">
              Results
              {result.row_count > 0 ? (
                <Badge size="sm" tone="neutral">
                  {result.row_count}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="plan">Plan</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* ---------------- results ---------------- */}
          <TabsContent value="results">
            <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2 text-caption text-muted">
                <span>
                  {result.row_count.toLocaleString()} row{result.row_count === 1 ? '' : 's'} in{' '}
                  <span className="font-mono text-foreground">{result.duration_ms}ms</span>
                  {result.truncated ? ' · truncated' : ''}
                </span>
                <CopyButton
                  value={toCsv(result)}
                  label="Copy CSV"
                />
              </div>

              {result.row_count === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted">
                  The statement ran successfully and returned no rows.
                </p>
              ) : (
                <div className="ms-scroll max-h-96 overflow-auto">
                  <table className="w-full border-collapse text-caption">
                    <thead>
                      <tr className="border-b border-line bg-sunken/60 text-left">
                        {result.columns.map((column) => (
                          <th
                            key={column.name}
                            scope="col"
                            className="whitespace-nowrap px-4 py-2 font-medium text-foreground"
                          >
                            {column.name}
                            <span className="ml-2 font-mono text-[10px] uppercase text-subtle">
                              {column.type}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-line last:border-0 hover:bg-sunken/40">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="whitespace-nowrap px-4 py-2 font-mono text-foreground">
                              {cell === null ? (
                                <span className="italic text-subtle">null</span>
                              ) : (
                                String(cell)
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pageCount > 1 ? (
                <div className="flex items-center justify-between border-t border-line px-4 py-2 text-caption text-muted">
                  <span>
                    Page {page + 1} of {pageCount}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </TabsContent>

          {/* ---------------- plan ---------------- */}
          <TabsContent value="plan">
            <PlanView plan={result.plan} />
          </TabsContent>

          {/* ---------------- history ---------------- */}
          <TabsContent value="history">
            {history.length === 0 ? (
              <EmptyState title="Nothing yet" description="Statements you run appear here." />
            ) : (
              <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface shadow-card">
                {history.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-start justify-between gap-4 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-caption text-foreground">
                        {entry.sql}
                      </p>
                      <p className="mt-0.5 text-[11px] text-subtle">
                        {entry.at} · {entry.row_count} rows
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="font-mono text-caption text-muted">{entry.duration_ms}ms</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSql(entry.sql)
                          runStatement(entry.sql)
                        }}
                      >
                        Rerun
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <EmptyState
          title="Run a query"
          description="Results, the execution plan, and history appear here."
        />
      )}
    </div>
  )
}

/**
 * The plan view describes work as steps — what was scanned, filtered,
 * aggregated — with timings. It deliberately reports no hardware detail.
 */
function PlanView({ plan }: { plan?: QueryPlan }) {
  if (!plan) {
    return (
      <Callout tone="info">
        The preview endpoint does not return a plan for this statement.
      </Callout>
    )
  }

  return (
    <div className="space-y-4 rounded-card border border-line bg-surface p-5 shadow-card">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <p className="text-caption font-medium uppercase tracking-widest text-subtle">Total time</p>
          <p className="mt-1 font-mono text-xl font-semibold text-foreground">{plan.total_ms}ms</p>
        </div>
        <div>
          <p className="text-caption font-medium uppercase tracking-widest text-subtle">Rows processed</p>
          <p className="mt-1 font-mono text-xl font-semibold text-foreground">
            {plan.rows_processed.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-caption font-medium uppercase tracking-widest text-subtle">Memory</p>
          <p className="mt-1 font-mono text-xl font-semibold text-foreground">{plan.memory_mb} MB</p>
        </div>
      </div>

      <ol className="space-y-2">
        {plan.steps.map((step, index) => (
          <li
            key={index}
            className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-sunken px-4 py-2.5"
          >
            <div className="flex min-w-0 items-baseline gap-3">
              <span className="font-mono text-caption text-subtle">{index + 1}.</span>
              <span className="text-sm font-medium text-foreground">{step.step}</span>
              <span className="truncate font-mono text-caption text-muted">{step.detail}</span>
            </div>
            <div className="flex shrink-0 items-center gap-4 font-mono text-caption text-muted">
              {step.rows !== null ? <span>{step.rows.toLocaleString()} rows</span> : null}
              <span className="text-foreground">{step.duration_ms}ms</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

function toCsv(result: QueryResult): string {
  if (result.rows.length === 0) return ''
  const escape = (value: unknown) => {
    if (value === null) return ''
    const text = String(value)
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
  }
  const header = result.columns.map((c) => escape(c.name)).join(',')
  const rows = result.rows.map((row) => row.map(escape).join(',')).join('\n')
  return `${header}\n${rows}`
}
